import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AnimatePresence, motion } from 'framer-motion'
import { KanbanCard, type Order } from './KanbanCard'
import { Skeleton } from './ui/skeleton'

interface ColumnProps {
  column: {
    id: string
    title: string
    color: string
  }
  orders: Order[]
  collaborators?: { id: string; name: string }[]
  onCheckoutClick?: (order: Order) => void
  onStatusChange?: (order: Order, newStatus: string) => void
  onAssignCollaborator?: (orderId: string, collabId: string) => void
  onWhatsAppClick?: (order: Order) => void
  isCompact?: boolean
  currentUserRole?: string
  isLoading?: boolean
}

export function KanbanColumn({
  column,
  orders,
  collaborators,
  onCheckoutClick,
  onStatusChange,
  onAssignCollaborator,
  onWhatsAppClick,
  isCompact,
  currentUserRole,
  isLoading,
}: ColumnProps) {
  const { setNodeRef } = useDroppable({
    id: column.id,
  })

  return (
    <div className="flex flex-col flex-shrink-0 w-[85vw] sm:w-80 bg-slate-900/40 backdrop-blur-md rounded-xl border border-slate-800 overflow-hidden shadow-xl h-full snap-center">
      <div
        className={`p-4 border-b border-slate-800 bg-slate-900/60 ${column.color} border-l-4 flex-shrink-0`}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-200">{column.title}</h3>
          <span className="bg-slate-800 text-slate-300 text-xs px-2 py-1 rounded-full font-medium">
            {isLoading ? <Skeleton className="h-4 w-6 rounded-full" /> : orders.length}
          </span>
        </div>
      </div>

      <div ref={setNodeRef} className="flex-1 p-3 min-h-[500px] overflow-y-auto space-y-3">
        {isLoading ? (
          // Skeleton loading state
          Array.from({ length: 3 }).map((_, i) => (
            <motion.div
              key={`skeleton-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-slate-800/50 rounded-lg p-4 space-y-3"
            >
              <div className="flex justify-between items-center">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-10 w-full" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            </motion.div>
          ))
        ) : (
          <SortableContext items={orders.map((o) => o.id)} strategy={verticalListSortingStrategy}>
            <AnimatePresence>
              {orders.map((order) => (
                <motion.div
                  key={order.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                  transition={{ duration: 0.3 }}
                >
                  <KanbanCard
                    order={order}
                    collaborators={collaborators}
                    onCheckoutClick={onCheckoutClick}
                    onStatusChange={onStatusChange}
                    onAssignCollaborator={onAssignCollaborator}
                    onWhatsAppClick={onWhatsAppClick}
                    isCompact={isCompact}
                    currentUserRole={currentUserRole}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </SortableContext>
        )}
      </div>
    </div>
  )
}
